/**
 * @file Atom: Button
 * @description A reusable, styled button component for consistent user interactions.
 * It supports various visual styles, sizes, and states (e.g., disabled).
 */

import type React from 'react';
import { twMerge } from 'tailwind-merge';
import { useSoundEffect } from '../../hooks/useSoundEffects';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?:
    | 'primary'
    | 'secondary'
    | 'subtle'
    | 'replay'
    | 'home'
    | 'next'
    | 'figma'
    | 'figma-secondary'
    | 'hint'
    | 'noir-primary'
    | 'noir-secondary'
    | 'noir-text'
    | 'title-primary'
    | 'title-secondary'
    | 'title-play'
    | 'title-more-cases';
  size?: 'sm' | 'md' | 'lg' | 'title';
  fullWidth?: boolean;
  /** Whether to play sound when clicked (default: true) */
  playSound?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  playSound = true,
  ...props
}) => {
  const baseClasses =
    ' rounded-none transition-all duration-300 uppercase cursor-pointer disabled:cursor-not-allowed';

  const variantClasses = {
    primary:
      'bg-red-600 text-white hover:bg-white hover:text-red-600 border-2 border-red-600 disabled:bg-black disabled:text-gray-500 disabled:border-gray-700',
    secondary:
      'bg-black text-gray-300 hover:bg-gray-600 disabled:bg-black disabled:text-gray-600',
    subtle:
      'bg-gray-600 text-gray-200 hover:bg-gray-500 disabled:bg-black disabled:text-gray-600',
    replay:
      'bg-cyan-500 text-white hover:bg-cyan-600 border-2 border-cyan-500 disabled:bg-black disabled:text-gray-500',
    home: 'bg-black text-white hover:bg-black border-2 border-gray-800 disabled:bg-black disabled:text-gray-600',
    next: 'bg-gray-300 text-black hover:bg-gray-200 border-2 border-gray-300 disabled:bg-black disabled:text-gray-500',
    figma:
      'bg-[rgba(95,244,255,0.3)] text-[#5ff4ff] hover:bg-transparent border border-[rgba(95,244,255,0.5)] shadow-[0px_0px_10px_0px_rgba(95,244,255,0.25)] font-["Russo_One",_sans-serif] disabled:bg-[#202020] disabled:text-gray-500 disabled:border-gray-700',
    'figma-secondary':
      'bg-[#202020] text-white hover:bg-[#303030] font-["Russo_One",_sans-serif] disabled:bg-[#202020] disabled:text-gray-600',
    hint: 'bg-white text-black hover:bg-gray-100 border border-white font-oswald text-base tracking-[1.6px] disabled:bg-gray-600 disabled:text-gray-400 disabled:border-gray-600 flex items-center justify-center',
    'noir-primary':
      'bg-white text-black hover:bg-gray-100 font-oswald text-[27px]  tracking-[1.6px] uppercase border-none shadow-none disabled:bg-gray-600 disabled:text-gray-400',
    'noir-secondary':
      'bg-transparent text-white hover:bg-white hover:text-black border border-white font-oswald text-[27px]  tracking-[1.6px] uppercase disabled:bg-transparent disabled:text-gray-400 disabled:border-gray-600',
    'noir-text':
      'bg-transparent text-white hover:bg-white hover:text-black border-none font-oswald text-[27px]  tracking-[1.6px] uppercase disabled:bg-transparent disabled:text-gray-400',
    'title-primary':
      'bg-white text-black hover:bg-black hover:text-white border border-white font-oswald text-[27px]  tracking-[1.6px] transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed',
    'title-secondary':
      'bg-white text-black hover:bg-black hover:text-white border border-white font-oswald text-[27px]  tracking-[1.6px] transition-all duration-300 ease-in-out',
    'title-play':
      'bg-transparent text-[#FF4426] hover:text-[#FF5733] border-none font-oswald font-medium text-[32px] tracking-[1.6px] transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed',
    'title-more-cases':
      'bg-transparent text-white hover:text-[rgba(255,255,255,0.7)] border-none font-oswald text-[32px] tracking-[1.6px] transition-all duration-300 ease-in-out',
  };

  const sizeClasses = {
    sm: 'px-4 py-2 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-[24px] w-full',
    title: 'w-[244px] h-auto flex items-center justify-center m-0 p-0',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  const combinedClasses = twMerge(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    widthClass,
    className,
  );

  // Sound effect hook
  const { play: playButtonSound } = useSoundEffect('BUTTON_PRESS');

  // Handle click with sound
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (playSound && !props.disabled) {
      playButtonSound();
    }

    if (props.onClick) {
      props.onClick(e);
    }
  };

  return (
    <button
      className={combinedClasses}
      {...props}
      onClick={handleClick}
      data-has-sound="true"
    >
      {children}
    </button>
  );
};

export default Button;
